import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { date, dateTime, mobile as fmtMobile, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Chips, Field, Input, Modal, Notice, SearchInput, Textarea, useToast } from '../components/ui';

interface Extension {
  id: string;
  originalEndsAt: string;
  previousEndsAt: string;
  newEndsAt: string;
  extensionDays: number;
  reason: string;
  createdAt: string;
  extendedBy: { id: string; displayName: string | null; email: string | null };
}

interface FreeSlotRow {
  id: string;
  status: 'ACTIVE' | 'EXPIRED';
  startsAt: string;
  endsAt: string;
  originalEndsAt: string;
  extensionCount: number;
  daysRemaining: number;
  slotsTotal: number;
  slotsUsed: number;
  listingsUsed: number;
  user: { id: string; mobile: string; displayName: string | null };
  extensions: Extension[];
}

type StatusFilter = '' | 'ACTIVE' | 'EXPIRED' | 'EXTENDED';

/**
 * The one free slot every account gets, and every time an admin extended one.
 *
 * Extending is a judgement — "no enquiries during the period" is not something
 * the system can decide — so the page shows the signal (listings posted
 * against the slot, days left) and makes the admin write down their reasoning.
 */
export function FreeSlotsPage() {
  const { get, set } = useListParams();
  const search = get('search');
  const status = (get('status') || '') as StatusFilter;
  const [extending, setExtending] = useState<FreeSlotRow | null>(null);
  const [history, setHistory] = useState<FreeSlotRow | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const columns: Array<Column<FreeSlotRow>> = [
    {
      key: 'user',
      header: 'Account',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.user.displayName ?? 'Unnamed'}</div>
          <div className="cell-muted">{fmtMobile(r.user.mobile)}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Free slot',
      render: (r) => (
        <Badge tone={r.status === 'ACTIVE' ? 'green' : 'red'} dot>
          {r.status === 'ACTIVE' ? 'Active' : 'Expired'}
        </Badge>
      ),
    },
    { key: 'start', header: 'Started', render: (r) => <span className="cell-muted">{date(r.startsAt)}</span> },
    {
      key: 'expiry',
      header: 'Expires',
      render: (r) => (
        <div>
          <div className="cell-strong">{date(r.endsAt)}</div>
          <div className="cell-muted">
            {r.status === 'ACTIVE' ? `${r.daysRemaining} days left` : 'lapsed'}
            {/* Only worth saying once it has actually moved. */}
            {r.extensionCount > 0 && ` · originally ${date(r.originalEndsAt)}`}
          </div>
        </div>
      ),
    },
    {
      key: 'extensions',
      header: 'Extensions',
      numeric: true,
      render: (r) =>
        r.extensionCount === 0 ? (
          <span className="cell-muted">None</span>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setHistory(r)}>
            {num(r.extensionCount)} ▸
          </Button>
        ),
    },
    {
      key: 'used',
      header: 'Listings posted',
      numeric: true,
      // The evidence behind "no customers during the period". Zero is the
      // case the extension rule was written for.
      render: (r) =>
        r.listingsUsed === 0 ? <Badge tone="amber">None yet</Badge> : <span>{num(r.listingsUsed)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="cell-actions">
          <Link to={`/users/${r.user.id}`} onClick={(e) => e.stopPropagation()}>
            <Button size="sm">Account</Button>
          </Link>
          <Button size="sm" variant="primary" onClick={() => setExtending(r)}>
            Extend
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack">
      <DataTable<FreeSlotRow>
        queryKey={['free-slots', search, status]}
        path="/admin/free-slots"
        params={{ search, status }}
        columns={columns}
        emptyIcon="◷"
        emptyTitle="No free slots yet"
        emptyBody="A free slot is granted the first time an account opens its slots, so accounts that have never looked are not listed here."
        footer={(page) => {
          const active = page.meta.active as number | undefined;
          const expired = page.meta.expired as number | undefined;
          const extended = page.meta.extended as number | undefined;
          const rules = page.meta.rules as
            | { durationDays: number; extensionDays: number; maxExtensions: number }
            | undefined;
          if (active === undefined || !rules) return null;
          return (
            <div
              className="row row--wrap"
              style={{ padding: '12px var(--s5)', borderTop: '1px solid var(--border)', gap: 'var(--s5)' }}
            >
              <span className="row" style={{ gap: 7 }}>
                <Badge tone="green" dot>
                  Active
                </Badge>
                <strong>{num(active)}</strong>
              </span>
              <span className="row" style={{ gap: 7 }}>
                <Badge tone="red" dot>
                  Expired
                </Badge>
                <strong>{num(expired ?? 0)}</strong>
              </span>
              <span className="row" style={{ gap: 7 }}>
                <Badge tone="blue" dot>
                  Extended at least once
                </Badge>
                <strong>{num(extended ?? 0)}</strong>
              </span>
              <span className="cell-muted">
                {rules.durationDays} days free · {rules.extensionDays} days per extension ·{' '}
                {rules.maxExtensions > 0 ? `${rules.maxExtensions} extensions maximum` : 'no extension limit'} ·{' '}
                <Link to="/settings">change →</Link>
              </span>
            </div>
          );
        }}
        toolbar={
          <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
            <Chips<StatusFilter>
              value={status}
              options={[
                { value: '', label: 'All' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'EXPIRED', label: 'Expired' },
                { value: 'EXTENDED', label: 'Extended' },
              ]}
              onChange={(v) => set('status', v)}
            />
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Name or mobile…" />
          </div>
        }
      />

      {extending && (
        <ExtendDialog
          row={extending}
          onClose={() => setExtending(null)}
          onDone={() => {
            setExtending(null);
            toast('Free slot extended', 'success');
            qc.invalidateQueries({ queryKey: ['free-slots'] });
          }}
        />
      )}

      {history && <HistoryDialog row={history} onClose={() => setHistory(null)} />}
    </div>
  );
}

/**
 * Owns its own form state.
 *
 * A controller created by the caller is disposed the moment the caller
 * re-renders without it, which has bitten this codebase before — the dialog
 * keeps what it needs.
 */
function ExtendDialog({ row, onClose, onDone }: { row: FreeSlotRow; onClose: () => void; onDone: () => void }) {
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');
  const toast = useToast();

  const extend = useMutation({
    mutationFn: () =>
      api.post(`/admin/free-slots/${row.id}/extend`, {
        ...(days.trim() ? { days: Number(days) } : {}),
        reason: reason.trim(),
      }),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const ready = reason.trim().length >= 3;

  return (
    <Modal
      title="Extend free slot"
      sub={`${row.user.displayName ?? 'Unnamed'} · currently ${row.status === 'ACTIVE' ? 'expires' : 'expired'} ${date(row.endsAt)}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={extend.isPending} disabled={!ready} onClick={() => extend.mutate()}>
            Extend
          </Button>
        </>
      }
    >
      <div className="stack">
        {row.status === 'EXPIRED' && (
          <Notice tone="info" title="This slot has already lapsed">
            The new expiry is counted from today, not from {date(row.endsAt)} — otherwise the extension would hand back
            days that have already gone.
          </Notice>
        )}

        <Field label="Days" hint="Leave blank to use the configured extension length.">
          <Input
            type="number"
            min={1}
            max={365}
            value={days}
            placeholder="Configured default"
            onChange={(e) => setDays(e.target.value)}
          />
        </Field>

        <Field label="Reason" hint="Recorded against your account and shown in the extension history. Required.">
          <Textarea
            rows={3}
            value={reason}
            placeholder="e.g. No enquiries received during the free period."
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

function HistoryDialog({ row, onClose }: { row: FreeSlotRow; onClose: () => void }) {
  return (
    <Modal
      wide
      title="Extension history"
      sub={`${row.user.displayName ?? 'Unnamed'} · granted ${date(row.startsAt)}, originally expiring ${date(row.originalEndsAt)}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>When</th>
              <th>By</th>
              <th>Was</th>
              <th>Became</th>
              <th style={{ textAlign: 'right' }}>Days</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {/* Newest first, as the API returns them. */}
            {row.extensions.map((x) => (
              <tr key={x.id}>
                <td className="cell-muted">{dateTime(x.createdAt)}</td>
                <td>{x.extendedBy.displayName ?? x.extendedBy.email ?? 'Unknown'}</td>
                <td className="cell-muted">{date(x.previousEndsAt)}</td>
                <td className="cell-strong">{date(x.newEndsAt)}</td>
                <td className="cell-num">+{x.extensionDays}</td>
                <td>{x.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
